import { List, Pressable, ThemeComponentSwitcher, View, fromTheme, h3, styled } from 'lib_ui-primitives';
import { memo, createElement as rc, useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import GridGL2025 from './Grid.gl2025';
import { getColumnHNodes, getColumnWidths } from './columns/useColumnWidths';

let StyledGrid = styled(View).attrs({ name: 'StyledGrid' })`
    border: none;
    flex-grow: 1;
    flex-direction: column;
    padding: 0;
`;

const GridTitle = styled(h3)`
    margin: 6px;
    color: ${({ theme }) => theme.defaultFontColor};
`;

const GridBody = styled(View).attrs({ name: 'GridBody' })`
    min-height: 100px;
    flex-grow: 1;
    flex-direction: row;
    box-shadow: none;
`;

let Row = styled(Pressable)`
    text-align: center;
    height: ${fromTheme('listLineHeight')};
    border-color: transparent;
    border-style: solid;
    border-top-width: 0;
    border-left-width: 0;
    border-right-width: 0;
    border-bottom-width: 0;
`;

// GridBody content switcher: single-grid (old) vs two-grid (GL2025)
const GridBodyContent = ThemeComponentSwitcher([
    { theme: 'gl2025', component: GridGL2025 },
    {
        theme: 'old', // Default/old theme
        component: function GridBodyOld(props) {
            const {
                id,
                records,
                recordCount,
                onClick,
                onRenderItem,
                RowDetail,
                HeaderRow,
                onScrollLayoutChange,
                endOfRecordsText,
                listType,
                hNode,
                headerClicked,
                sortedColumns,
                columnHNodes,
                handleColumnWidthChange
                // columnWidths not used in old grid but passed for consistency
            } = props;

            return rc(
                List,
                {
                    id,
                    onScrollLayoutChange,
                    data: records,
                    itemCount: recordCount,
                    onClick,
                    onRenderItem,
                    Row,
                    RowDetail,
                    listType,
                    endOfRecordsText
                },
                rc(HeaderRow, { hNode, headerClicked, sortedColumns, columnHNodes, handleColumnWidthChange })
            );
        }
    }
]);

function Grid(props) {
    const {
        hNode,
        hNode: { title, id },
        listType,
        records,
        recordCount,
        sortedColumns,
        columnHNodes: _columnHNodes,
        headerClicked,
        onClick,
        onRenderItem,
        className,
        style,
        RowDetail,
        HeaderRow,
        onScrollLayoutChange,
        endOfRecordsText,
        awaitingFirstServerPage,
        localOnlySearchReason,
        offlineText,
        handleColumnWidthChange
    } = props || { hNode: {} };

    const theme = useContext(ThemeContext);

    // Process column configurations
    const columnHNodes = useMemo(() => getColumnHNodes(_columnHNodes, hNode.children), [_columnHNodes, hNode.children]);

    const columnWidths = useMemo(() => getColumnWidths(columnHNodes, theme), [columnHNodes, theme]);

    // prettier-ignore
    return rc(StyledGrid, { id: 'grid' + id, name: 'grid', className, style, 'data-test-count': recordCount, role: 'grid', 'aria-label': hNode.relation ?? 'data grid' },
        title && title.length ? rc(GridTitle, { name: 'grid-title' }, title) : null,
        rc(GridBody, { id: `grid-body-${id}`, name: 'grid-body' },
            rc(GridBodyContent, {
                id,
                records,
                recordCount,
                columnHNodes,
                columnWidths,
                RowDetail,
                HeaderRow,
                onClick,
                onRenderItem,
                onScrollLayoutChange,
                endOfRecordsText,
                awaitingFirstServerPage,
                localOnlySearchReason,
                offlineText,
                listType,
                hNode,
                headerClicked,
                sortedColumns,
                handleColumnWidthChange
            })
        )
    );
}
const _Grid = memo(Grid);
_Grid.displayName = 'MemoizedGrid';
export default _Grid;
